import {
  Stack,
  Typography,
  Card,
  CardContent,
  Box,
  Skeleton,
  TextField,
  InputAdornment,
  Tab,
  Tabs,
  useTheme,
  Divider,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { GenericTable } from "@/components/GenericTable/GenericTable";

// Função para calcular a largura ideal do eixo Y baseada no tamanho dos labels
const calculateYAxisWidth = (data: { label: string; value: number }[]) => {
  if (!data || data.length === 0) return 150;
  const maxLabelLength = Math.max(...data.map((item) => item.label.length));
  const calculatedWidth = Math.max(150, maxLabelLength * 7 + 20);
  return Math.min(calculatedWidth, 400);
};
import SearchIcon from "@mui/icons-material/Search";
import { GetProtocols } from "@/services/fhir/questionnaireresponse/getProtocols";
import { GetQuestionnaireResponse } from "@/services/fhir/questionnaireresponse/GetQuestionnaries";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { oids } from "@/configs/Settings";
import { useAdministrativeFilters } from "@/pages/adminInsights/shared/useAdministrativeFilters";
import { useDateRangeFilter } from "@/components/common/DateRangeFilter/useDateRangeFilter";
import { NoData } from "@/components/common/NoData";
import { ViewHeader } from "@/components/schedule/components/ViewHeader";
import { DateFilterType } from "@/utils/interfaces";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`protocol-tabpanel-${index}`}
      aria-labelledby={`protocol-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

interface ProtocolData {
  protocolId: string;
  label: string;
  count: number;
}

export function ProtocolAnalysis({
  count,
  data,
}: {
  count?: number;
  data?: any;
}) {
  const theme = useTheme();
  const { organizationId } = useOrganizationContext();
  const [currentTab, setCurrentTab] = useState(0);
  const [search, setSearch] = useState<string>("");
  const [protocolSearch, setProtocolSearch] = useState<string>("");
  const [protocols, setProtocols] = useState<ProtocolData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Filtros de data
  const {
    selectedDate,
    dateFilterType,
    dateRange,
    handleDateChange,
    handleFilterTypeChange,
  } = useDateRangeFilter({ defaultFilterType: "month" });

  // Converte dateFilterType para DateFilterType (removendo "year" se necessário)
  const filterType: DateFilterType =
    dateFilterType === "year" ? "month" : (dateFilterType as DateFilterType);

  // Wrapper para handleFilterTypeChange que converte "year" para "month"
  const handleFilterTypeChangeWrapper = (type: DateFilterType) => {
    handleFilterTypeChange(type as any);
  };

  // Filtros administrativos
  const {
    selectedLocation,
    selectedPractitioner,
    handleLocationChange,
    handlePractitionerChange,
    location,
    locationLoading,
    practitionerRole,
    practitioner,
    practitionerRoleLoading,
  } = useAdministrativeFilters({
    onChange: (filters) => {
      console.log("Filtros de protocolos atualizados:", filters);
    },
  });

  const author = `${oids.cnpj}-${organizationId}`;
  // Mantém "all" para que GetQuestionnaires reconheça e busque todos os profissionais
  const practitionerFilter = selectedPractitioner || "all";

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Função para resetar todos os filtros
  const handleReset = () => {
    handleLocationChange("");
    handlePractitionerChange("");
  };

  // Buscar dados dos protocolos
  const fetchProtocolsData = useCallback(async () => {
    if (!author) return;

    setLoading(true);
    try {
      // 1) Buscar a lista de protocolos
      const protocolsBundle = await GetProtocols({ author });
      const protocolEntries: any[] = protocolsBundle?.entry || [];

      const protocolsList = protocolEntries
        .map((e: any) => e?.resource)
        .filter((r: any) => r && r.resourceType === "QuestionnaireResponse")
        .map((r: any) => ({ id: r.id as string, resource: r }))
        .filter((r: any) => !!r.id);

      if (!protocolsList.length) {
        setProtocols([]);
        setLoading(false);
        return;
      }

      // 2) Para cada protocolo, buscar a contagem
      // Passa "all" para GetQuestionnaires quando quer todos os profissionais
      const results = await Promise.allSettled(
        protocolsList.map(async (p: any) => {
          const summary = await GetQuestionnaireResponse({
            author,
            practitioner: practitionerFilter,
            hasProtocols: true,
            protocol: p.id,
            count_num: true,
            startDate: dateRange?.startDate,
            endDate: dateRange?.endDate,
          });

          const total: number = Number.isFinite(summary?.total)
            ? summary.total
            : 0;

          // Busca o nome do protocolo no item com linkId "438586562091"
          const nameItem = p.resource?.item?.find(
            (item: any) => item.linkId === "438586562091"
          );
          const protocolName =
            nameItem?.answer?.[0]?.valueString ||
            p.resource?.title ||
            p.resource?.name ||
            `Protocolo ${p.id}`;

          const label: string =
            typeof protocolName === "string"
              ? protocolName
              : `Protocolo ${p.id}`;

          return {
            protocolId: p.id,
            label,
            count: total,
          };
        })
      );

      const okProtocols: ProtocolData[] = results
        .filter(
          (r): r is PromiseFulfilledResult<ProtocolData> =>
            r.status === "fulfilled"
        )
        .map((r) => r.value)
        .filter((protocol) => protocol.count > 0) // Apenas protocolos com pelo menos 1 resposta
        .sort((a, b) => b.count - a.count);

      setProtocols(okProtocols);
    } catch (error) {
      console.error("Erro ao buscar protocolos:", error);
      setProtocols([]);
    } finally {
      setLoading(false);
    }
  }, [author, practitionerFilter, dateRange?.startDate, dateRange?.endDate]);

  useEffect(() => {
    if (author) {
      fetchProtocolsData();
    }
  }, [fetchProtocolsData, author]);

  // Processar dados para visualização
  const { totalProtocols, protocolsList } = useMemo(() => {
    const total = protocols.reduce((sum, p) => sum + p.count, 0);
    return {
      totalProtocols: total,
      protocolsList: protocols,
    };
  }, [protocols]);

  // Dados para o gráfico de barras (todos os protocolos)
  const chartData = useMemo(() => {
    return protocols
      .filter((p) => p.count > 0)
      .map((protocol, idx) => ({
        id: idx,
        value: protocol.count,
        label: protocol.label,
      }))
      .sort((a, b) => b.value - a.value);
  }, [protocols]);

  // Top 5 protocolos
  const topProtocolsData = useMemo(() => {
    return chartData.slice(0, 5);
  }, [chartData]);

  // Filtrar protocolos para a tabela de pesquisa
  const filteredProtocolsData = useMemo(() => {
    if (!protocolSearch.trim()) return chartData;
    const q = protocolSearch.toLowerCase();
    return chartData.filter((item) => item.label.toLowerCase().includes(q));
  }, [chartData, protocolSearch]);

  // Filtrar dados baseado na busca
  const filteredProtocols = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return protocolsList;
    return protocolsList.filter((protocol) =>
      protocol.label?.toLowerCase().includes(q)
    );
  }, [protocolsList, search]);

  // Renderizar skeleton durante carregamento
  if (loading) {
    return (
      <Stack spacing={3} width="100%">
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Skeleton variant="text" width={200} height={32} />
              <Skeleton variant="rectangular" height={250} />
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack width="100%" spacing={3}>
      {/* Barra de Filtros */}
      <ViewHeader
        title="Protocolos"
        dateFilter={selectedDate}
        filterType={filterType}
        setDateFilter={handleDateChange}
        setFilterType={handleFilterTypeChangeWrapper}
        showAdminFilters={true}
        adminFiltersProps={{
          selectedLocation,
          selectedPractitioner,
          onLocationChange: handleLocationChange,
          onPractitionerChange: handlePractitionerChange,
          location,
          locationLoading,
          practitionerRole,
          practitioner,
          practitionerRoleLoading,
          onResetFilters: () => {
            handleLocationChange("");
            handlePractitionerChange("");
          },
          setIsSearchActive: () => {},
        }}
        onReset={handleReset}
        resetButtonProps={{
          tooltip: "Limpar todos os filtros",
        }}
      />

      {totalProtocols > 0 ? (
        <>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={currentTab} onChange={handleTabChange}>
              <Tab label="Protocolos" />
              <Tab label="Detalhamento" />
            </Tabs>
          </Box>

          <TabPanel value={currentTab} index={0}>
            <Card>
              <CardContent>
                {topProtocolsData.length > 0 ? (
                  <Box>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: "bold" }}
                    >
                      {(() => {
                        const total = chartData.reduce(
                          (acc, item) => acc + item.value,
                          0
                        );
                        const percentage =
                          total > 0
                            ? Math.round(
                                (topProtocolsData[0].value / total) * 100
                              )
                            : 0;
                        return `'${topProtocolsData[0].label}' representa ${percentage}% dos protocolos aplicados`;
                      })()}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      paragraph
                      sx={{ fontSize: "0.9rem" }}
                    >
                      Veja os 5 protocolos clínicos mais utilizados. Use a
                      tabela abaixo para pesquisar todos os protocolos
                      aplicados.
                    </Typography>
                    <Box sx={{ height: 300, width: "100%", mt: 2 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={topProtocolsData}
                          layout="vertical"
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" allowDecimals={false} />
                          <YAxis
                            dataKey="label"
                            type="category"
                            width={calculateYAxisWidth(topProtocolsData)}
                            tick={{ fontSize: 12 }}
                          />
                          <RechartsTooltip
                            formatter={(value: number | undefined) => {
                              if (value === undefined)
                                return ["0", "Quantidade"];
                              const total = chartData.reduce(
                                (acc, item) => acc + item.value,
                                0
                              );
                              const percentage =
                                total > 0
                                  ? Math.round((value / total) * 100)
                                  : 0;
                              return [
                                `${value} (${percentage}%)`,
                                "Quantidade",
                              ];
                            }}
                          />
                          <Bar dataKey="value" name="Quantidade">
                            {topProtocolsData.map((entry, index) => {
                              const maxValue = Math.max(
                                ...topProtocolsData.map((d) => d.value)
                              );
                              const isHighlight = entry.value === maxValue;
                              return (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={
                                    isHighlight
                                      ? theme.palette.primary.main
                                      : "#9E9E9E"
                                  }
                                />
                              );
                            })}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>

                    {/* Tabela de todos os protocolos */}
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Todos os Protocolos ({chartData.length} tipos
                        diferentes)
                      </Typography>
                      <Box sx={{ mb: 2 }}>
                        <TextField
                          size="small"
                          variant="outlined"
                          fullWidth
                          placeholder="Buscar protocolo..."
                          value={protocolSearch}
                          onChange={(e) => setProtocolSearch(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                      <GenericTable
                        tableColumns={[
                          {
                            column: "Protocolo",
                            rowName: "label",
                            align: "left",
                          },
                          {
                            column: "Quantidade",
                            rowName: "value",
                            width: "120px",
                          },
                          {
                            column: "Percentual",
                            rowName: "percentage",
                            width: "120px",
                          },
                        ]}
                        tableRows={filteredProtocolsData.map((item) => {
                          const total = chartData.reduce(
                            (acc, i) => acc + i.value,
                            0
                          );
                          const percentage =
                            total > 0
                              ? Math.round((item.value / total) * 100)
                              : 0;
                          return {
                            ...item,
                            percentage: `${percentage}%`,
                          };
                        })}
                        characterLimit={80}
                      />
                    </Box>
                  </Box>
                ) : (
                  <NoData message="Nenhum protocolo encontrado." />
                )}
              </CardContent>
            </Card>
          </TabPanel>

          <TabPanel value={currentTab} index={1}>
            <Card>
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  <TextField
                    size="small"
                    variant="outlined"
                    fullWidth
                    placeholder="Buscar por protocolo"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <GenericTable
                  tableColumns={[
                    { column: "Protocolo", rowName: "label", align: "left" },
                    { column: "Quantidade", rowName: "count", width: "150px" },
                  ]}
                  tableRows={filteredProtocols}
                  characterLimit={60}
                />
              </CardContent>
            </Card>
          </TabPanel>
        </>
      ) : (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            Nenhum protocolo encontrado
          </Typography>
        </Box>
      )}
    </Stack>
  );
}
